/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import productSearchService from 'service/productSearch';
import systemProductService from 'service/systemProduct';
import SearchProductToStore from './SearchProductToStore';
import legalEntityCurrencyService from 'service/legalEntityCurrency';

interface AddProductToStoreProps {
  setOpenAddProduct: (show: boolean) => void;
  refetchActive: () => void;
}

const AddProductToStore = ({
  setOpenAddProduct,
  refetchActive
}: AddProductToStoreProps) => {
  const { t, i18n } = useTranslation();

  const dispatch: Dispatch<any> = useDispatch();
  const inputBarcodeRef = useRef<HTMLInputElement>(null);
  const { register, control, reset, getValues } = useForm();

  const [openSearch, setOpenSearch] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [disabledSearchinput, setDisabledSearchInput] = useState(false);
  const [currencyOption, setCurrencyOption] = useState<any>([]);

  //GET Currency Entity to options
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.id,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  const handleGetProduct = (e: any) => {
    if (e.key === 'Enter') {
      if (searchedValue !== '') {
        setDisabledSearchInput(true);
        productSearchService
          .getListProduct({
            offset: 0,
            limit: 20,
            barcode: searchedValue ? searchedValue : undefined
          })
          .then((res: any) => {
            if (res?.products) {
              const computed = {
                name: res?.products?.[0]?.name ?? '',
                barcode: res?.products?.[0]?.barcode ?? '',
                barcode_original:
                  res?.products?.[0]?.nomenclature?.barcode_original,
                nomenclature_id: res?.products?.[0]?.nomenclature_id,
                quantity: res?.products?.[0]?.total_quantity ?? 0,
                price: res?.products?.[0]?.price ?? 0,
                price_in: res?.products?.[0]?.price_in ?? 0,
                price_wholesale: res?.products?.[0]?.price_whosale ?? 0,
                description: res?.products?.[0]?.description ?? '',
                sale_with_partion_code:
                  res?.products?.[0]?.partion_codes?.length > 0 ? true : false
              };

              reset(computed);
              setDisabledSearchInput(false);
            } else {
              dispatch(showAlert({ title: t('barcode_not_found') }));
              reset({
                name: '',
                barcode: '',
                barcode_original: '',
                nomenclature_id: '',
                quantity: '',
                currency: '',
                price: '',
                price_in: '',
                price_wholesale: '',
                description: '',
                sale_with_partion_code: false
              });
              setDisabledSearchInput(false);
            }
          });
      }
    }
  };

  const onSubmit = () => {
    const data = getValues();

    setLoading(true);

    if (data?.name == null || data?.name === '') {
      dispatch(
        showAlert({
          title: t('product_not_added')
        })
      );
      setLoading(false);
      return;
    }

    if (data?.currency_id == null || data?.currency_id === '') {
      dispatch(
        showAlert({
          title: t('currency')
        })
      );
      setLoading(false);
      return;
    }

    const MS_IN_DAY = 24 * 60 * 60 * 1000;
    const MS_IN_MONTH = 30 * MS_IN_DAY;
    const warrantyInMs = data?.warranty_period
      ? Number(data.warranty_period) * MS_IN_MONTH
      : 0;

    const createdData = {
      name: data?.name,
      barcode: data?.barcode,
      barcode_original: data?.barcode_original,
      nomenclature_id: data?.nomenclature_id,
      quantity: data?.quantity,
      price: data?.price,
      price_in: data?.price_in,
      price_wholesale: data?.price_wholesale,
      description: data?.description ?? '',
      currency_id: data?.currency_id ?? '',
      sale_with_partion_code: data?.sale_with_partion_code ?? false,
      usage_conditions: data?.usage_conditions ?? '',
      warranty_period: warrantyInMs > 0 ? warrantyInMs : 0
    };

    systemProductService
      .createSystemProduct(createdData)
      .then((res: any) => {
        if (res) {
          dispatch(
            showAlert({ title: t('successfully_added'), type: 'success' })
          );
          refetchActive();
          setOpenAddProduct(false);
          setLoading(false);
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            title: t('error_saving')
          })
        );
        setLoading(false);
      });
  };
  useEffect(() => {
    if (inputBarcodeRef.current) {
      inputBarcodeRef.current.focus();
    }
  }, []);

  return (
    <>
      <Form
        noValidate
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Row className="mb-2">
          <Col className="modal-rows">
            <Controller
              name="scan"
              control={control}
              render={({ field }) => (
                <Form.Floating className="first-item">
                  <Form.Control
                    {...field}
                    ref={inputBarcodeRef}
                    type="text"
                    value={searchedValue}
                    placeholder={t('scan_barcode')}
                    onChange={e => setSearchedValue(e?.target?.value)}
                    onKeyDown={e => handleGetProduct(e)}
                    onFocus={e => {
                      e.target.select();
                    }}
                    autoComplete="off"
                    disabled={disabledSearchinput}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('scan_barcode')}
                  </label>
                </Form.Floating>
              )}
            />

            <div className="d-flex justify-content-between gap-2">
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  setOpenSearch(true);
                }}
              >
                {t('search')}
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('name')}
                type="text"
                placeholder={t('product_names')}
                disabled={true}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">{t('product_names')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('quantity')}
                type="text"
                placeholder={t('quantity')}
                disabled={true}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">{t('quantity')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('price')}
                type="text"
                placeholder={t('price')}
                disabled={true}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">{t('price')}</label>
            </Form.Floating>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('price_in')}
                type="text"
                placeholder={t('price_admission')}
                disabled={true}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">
                {t('price_admission')}
              </label>
            </Form.Floating>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('price_wholesale')}
                type="text"
                placeholder={t('wholesale_price')}
                disabled={true}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">
                {t('wholesale_price')}
              </label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="currency_id"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field}>
                      <option className="d-none" value=""></option>
                      {currencyOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <label htmlFor="floatingInputCustom">{t('currency')}</label>
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('warranty_period')}
                type="number"
                placeholder={t('warranty_period')}
                onFocus={e => e.target.select()}
                autoComplete="off"
              />
              <label htmlFor="floatingInputCustom">
                {`${t('warranty_period')} (${t('month')})`}
              </label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('usage_conditions')}
                as="textarea"
                style={{ height: '100px' }}
                placeholder={t('usage_conditions')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">
                {t('usage_conditions')}
              </label>
            </Form.Floating>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('description')}
                as="textarea"
                style={{ height: '100px' }}
                placeholder={t('descriptions')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('descriptions')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="button"
          loading={loading}
          loadingPosition="start"
          onClick={() => onSubmit()}
        >
          {t('add_product')}
        </Button>

        <Modal
          show={openSearch}
          onHide={() => setOpenSearch(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('search_product')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SearchProductToStore
              setOpenSearch={setOpenSearch}
              reset={reset}
              setSearchedValue={setSearchedValue}
            />
          </Modal.Body>
        </Modal>
      </Form>
    </>
  );
};

export default AddProductToStore;
